"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";

import { Icon } from "../../icons";
import type { QuickAction } from "../../types";
import { cn } from "../../utils/cn";

type QuickActionsModalProps = {
  actions: QuickAction[];
  locale: string;
  onClose: () => void;
};

const cardClassName =
  "flex items-center gap-4 rounded-2xl bg-surface p-4 transition-all hover:shadow-shell-lg sm:flex-col sm:items-center sm:gap-3 sm:p-6 sm:text-center";

export function QuickActionsModal({
  actions,
  locale,
  onClose,
}: QuickActionsModalProps) {
  const t = useTranslations();

  return (
    <div
      data-testid="quick-actions"
      className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-3 sm:gap-4"
    >
      {actions.map((action) => {
        const body = (
          <>
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-light sm:h-14 sm:w-14">
              <span className="text-primary">
                <Icon name={action.icon} size={24} />
              </span>
            </div>
            <div className="sm:text-center">
              <span className="block text-base font-semibold text-text-primary sm:text-lg">
                {t(action.titleKey)}
              </span>
              {action.descriptionKey && (
                <span className="block text-sm text-text-secondary sm:text-base">
                  {t(action.descriptionKey)}
                </span>
              )}
            </div>
          </>
        );

        if (action.href !== undefined) {
          return (
            <Link
              key={action.titleKey}
              href={`/${locale}${action.href}`}
              onClick={onClose}
              data-testid="quick-actions-item"
              className={cardClassName}
            >
              {body}
            </Link>
          );
        }

        return (
          <button
            key={action.titleKey}
            type="button"
            data-testid="quick-actions-item"
            onClick={() => {
              onClose();
              action.onSelect();
            }}
            className={cn(cardClassName, "text-left")}
          >
            {body}
          </button>
        );
      })}
    </div>
  );
}
